/**
 * PXC money primitives.
 *
 * ADR-002: every amount in this system is an **integer number of mPXC**
 * (milli-PXC). Floating point never touches a balance. These helpers are shared
 * so the client formats exactly the way the server does — but note that the
 * *earning formula* deliberately lives on the server only (docs/ARCHITECTURE.md §7).
 */
export const MPXC_PER_PXC = 1000;
export const PXC_DECIMALS = 3;
/** Above this the 53-bit integer guarantee starts to matter; we refuse earlier. */
export const MAX_SAFE_MPXC = 9_007_199_254_740_000;
export class MoneyError extends Error {
    reason;
    constructor(message, reason) {
        super(message);
        this.reason = reason;
        this.name = 'MoneyError';
    }
}
/** Throws unless `value` is a safe, non-negative integer amount of mPXC. */
export function assertSafeAmount(value, label = 'amount') {
    if (!Number.isFinite(value) || !Number.isInteger(value)) {
        throw new MoneyError(`${label} must be an integer number of mPXC`, 'NOT_A_NUMBER');
    }
    if (value < 0)
        throw new MoneyError(`${label} must not be negative`, 'NEGATIVE');
    if (value > MAX_SAFE_MPXC)
        throw new MoneyError(`${label} exceeds the safe range`, 'TOO_LARGE');
    return value;
}
export function pxcToMpxc(pxc) {
    return Math.round(pxc * MPXC_PER_PXC);
}
export function mpxcToPxc(mpxc) {
    return mpxc / MPXC_PER_PXC;
}
const AMOUNT_PATTERN = /^\d{1,15}(?:\.\d{0,3})?$/;
/**
 * Parses user input ("12.5", "1,204.25") into integer mPXC.
 * Rejects exponent notation, negatives, and more than 3 decimals rather than
 * silently rounding money the user did not intend to spend.
 */
export function parsePxcToMpxc(raw) {
    const cleaned = raw.trim().replace(/,/g, '').replace(/\s/g, '');
    if (cleaned.length === 0)
        throw new MoneyError('Enter an amount', 'EMPTY');
    if (!AMOUNT_PATTERN.test(cleaned)) {
        if (/^\d+(\.\d+)?$/.test(cleaned)) {
            throw new MoneyError(`Use at most ${PXC_DECIMALS} decimals`, 'TOO_PRECISE');
        }
        throw new MoneyError('Enter a valid amount', 'NOT_A_NUMBER');
    }
    const [whole = '0', frac = ''] = cleaned.split('.');
    const mpxc = Number(whole) * MPXC_PER_PXC + Number(frac.padEnd(PXC_DECIMALS, '0'));
    return assertSafeAmount(mpxc, 'amount');
}
function groupThousands(digits) {
    return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}
/**
 * Formats mPXC for display. Truncates rather than rounds up, so a displayed
 * balance is never larger than the balance that actually exists.
 */
export function formatMpxc(mpxc, options = {}) {
    const { decimals = 2, signed = false, compact = false, unit = false } = options;
    const negative = mpxc < 0;
    const abs = Math.abs(Math.trunc(mpxc));
    let body;
    if (compact && abs >= 1_000_000 * MPXC_PER_PXC) {
        body = `${trimZeros((abs / (1_000_000 * MPXC_PER_PXC)).toFixed(2))}M`;
    }
    else if (compact && abs >= 1_000 * MPXC_PER_PXC) {
        body = `${trimZeros((abs / (1_000 * MPXC_PER_PXC)).toFixed(1))}K`;
    }
    else {
        const whole = Math.trunc(abs / MPXC_PER_PXC);
        const frac = abs % MPXC_PER_PXC;
        const clamped = Math.min(Math.max(decimals, 0), PXC_DECIMALS);
        const fracStr = clamped === 0 ? '' : `.${String(frac).padStart(PXC_DECIMALS, '0').slice(0, clamped)}`;
        body = `${groupThousands(String(whole))}${fracStr}`;
    }
    const sign = negative ? '-' : signed ? '+' : '';
    return `${sign}${body}${unit ? ' Gems' : ''}`;
}
function trimZeros(value) {
    return value.replace(/\.?0+$/, '');
}
/** "100.00 PXC/h" — the rate label used on miner tiles and the summary. */
export function formatRate(mpxcPerHour, options = {}) {
    return `${formatMpxc(mpxcPerHour, { decimals: 0, ...options })} Gems/h`;
}
//------------------------------------------------------------------------------
// Any asset, in its base units (USDT 6 decimals, TON 9, PXC 3)
//------------------------------------------------------------------------------
/**
 * Parses user input into integer base units for an asset with `decimals`
 * decimals. Same rules as parsePxcToMpxc: no exponents, no negatives, no more
 * precision than the asset has.
 */
export function parseUnits(raw, decimals) {
    const cleaned = raw.trim().replace(/,/g, '').replace(/\s/g, '');
    if (cleaned.length === 0)
        throw new MoneyError('Enter an amount', 'EMPTY');
    const pattern = new RegExp(`^\\d{1,12}(?:\\.\\d{0,${decimals}})?$`);
    if (!pattern.test(cleaned)) {
        if (/^\d+(\.\d+)?$/.test(cleaned)) {
            throw new MoneyError(`Use at most ${decimals} decimals`, 'TOO_PRECISE');
        }
        throw new MoneyError('Enter a valid amount', 'NOT_A_NUMBER');
    }
    const [whole = '0', frac = ''] = cleaned.split('.');
    const units = Number(whole) * 10 ** decimals + Number(frac.padEnd(decimals, '0') || '0');
    return assertSafeAmount(units, 'amount');
}
/** Formats base units, truncating (never rounding up) to `display` decimals. */
export function formatUnits(units, decimals, display = Math.min(decimals, 2)) {
    const negative = units < 0;
    const abs = Math.abs(Math.trunc(units));
    const scale = 10 ** decimals;
    const whole = Math.trunc(abs / scale);
    const frac = abs % scale;
    const shown = Math.min(Math.max(display, 0), decimals);
    const fracStr = shown === 0 ? '' : `.${String(frac).padStart(decimals, '0').slice(0, shown)}`;
    return `${negative ? '-' : ''}${groupThousands(String(whole))}${fracStr}`;
}
//# sourceMappingURL=money.js.map